import { Alert, Button, Chip, Grid, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useContext, useEffect, useState } from "react";
import { useDebounce } from "../../hooks/useDebounce";
import {
    Device_Service,
    Device_Status,
    Get_Devices_Filter,
} from "../../services/device/device-service";
import { PERMISSIONS } from "../../utils/constants";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import { Search } from "../filters/Search";
import New_Item from "../new-item";
import { RUDTable } from "../Table/RUDTable";
import UserContext, { User_Type } from "../UserContext";
import Create_Device_Form from "./create-device-form";
import Sync_Device_Attendance from "./sync-device-attendance";
import Sync_Device_Users from "./sync-device-users";

const initial_filter: Omit<Get_Devices_Filter, "page" | "limit"> & {
    org_code: string;
    apiKey: string;
} = { org_code: "", apiKey: "" };

export default function Devices_Page() {
    const [filter, set_filter] = useState(initial_filter);
    const { user } = useContext(UserContext);
    const debounce = useDebounce();

    useEffect(() => {
        if (user?.user_type === User_Type.Admin) {
            set_filter((f) => ({
                ...f,
                org_code: user.organization.code,
                apiKey: user.organization.apiKey[0].key,
            }));
        }
    }, [user?.user_type]);
    return (
        <Box>
            <Typography
                pt={2}
                px={2}
                color={(theme) => theme.palette.primary.main}
                variant="h5"
            >
                Devices
            </Typography>

            {user?.user_type === User_Type.Superadmin && (
                <New_Item title="Create Device">
                    <Create_Device_Form />
                </New_Item>
            )}

            <Grid container p={2} spacing={2}>
                {user?.user_type === User_Type.Superadmin && (
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            label="Organization"
                            labelKey="name"
                            api="/organization"
                            setOutput={(org) => {
                                set_filter((f) => ({
                                    ...f,
                                    org_code: org?.code || "",
                                }));
                            }}
                        />
                    </Grid>
                )}

                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        key={`branch-input`}
                        label="Branch"
                        labelKey="name"
                        setOutput={(branch) => {
                            set_filter((curr) => ({
                                ...curr,
                                branchId: branch?.id ?? "",
                            }));
                        }}
                        api={
                            user?.user_type === User_Type.Admin
                                ? "/organization/branch"
                                : "/organization/branch/superadmin"
                        }
                        apiParams={{
                            organization:
                                user?.user_type === User_Type.Admin
                                    ? user.organization.code
                                    : filter.org_code,
                        }}
                        request_headers={{
                            "x-org":
                                user?.user_type === User_Type.Admin
                                    ? user.organization.code
                                    : filter.org_code,
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Search filter={filter} setFilter={set_filter} />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Button
                        color="primary"
                        fullWidth
                        variant="contained"
                        onClick={() =>
                            set_filter({
                                ...filter,
                                apply: (filter.apply ?? 0) + 1,
                            })
                        }
                        style={{ height: "100%" }}
                        size="small"
                    >
                        {" "}
                        Search
                    </Button>
                </Grid>
            </Grid>

            {!filter.org_code ? (
                <Box px={2}>
                    <Alert severity="info">
                        Provide an organization to retrieve data for
                    </Alert>
                </Box>
            ) : (
                <RUDTable
                    updatingAgents={[filter.apply]}
                    getter={(page, limit) => {
                        const handler =
                            user?.user_type === User_Type.Superadmin
                                ? Device_Service.get_superadmin_devices
                                : Device_Service.get_devices;

                        return handler(filter.org_code, filter.apiKey as any, {
                            page,
                            limit,
                            branchId: filter.branchId,
                            search: filter.search,
                        });
                    }}
                    deletor={(id) => {
                        return Device_Service.delete_device(
                            id.toString(),
                            filter.org_code
                        );
                    }}
                    editor={(id, updated_org, row) => {
                        return Device_Service.edit_device(
                            row.serial,
                            updated_org,
                            user?.user_type === User_Type.Admin
                                ? user.organization.code
                                : filter.org_code,
                            user?.user_type === User_Type.Admin
                                ? user.organization.apiKey[0].key
                                : ""
                        );
                    }}
                    rowsPreprocessor={(row) => ({
                        ...row,
                        deletionId: row.serial,
                        default_status: { label: row.status, id: row.status },
                        branch: row.branch.name,
                        default_branch: row.branch,
                        default_organization: row.organization,
                        _status: (
                            <Chip
                                variant="outlined"
                                size="small"
                                // icon={<Circle fontSize="small" />}
                                sx={{ textTransform: "capitalize" }}
                                label={row.status}
                                color={
                                    row.status === Device_Status.Offline
                                        ? "error"
                                        : "success"
                                }
                            />
                        ),
                        organization: row.organization.name,
                    })}
                    ops={{
                        read:
                            user?.user_type === User_Type.Superadmin
                                ? ""
                                : PERMISSIONS.READ_DEVICE,
                        edit:
                            user?.user_type === User_Type.Superadmin
                                ? ""
                                : PERMISSIONS.UPDATE_DEVICE,
                        delete:
                            user?.user_type === User_Type.Admin
                                ? PERMISSIONS.ARCHIVE_BRANCH
                                : "",
                    }}
                    readables={{
                        serial: "Serial",
                        name: "Name",
                        platform: "Platform",
                        firmwareVersion: "Firmware Version",
                        branch: "Branch",
                        organization: "Orgainzation",
                        _status: "Status",
                        ...(user?.user_type === User_Type.Superadmin
                            ? { sync_device: "Sync Users / Attendance" }
                            : {}),
                    }}
                    editables={[
                        {
                            isRequired: true,
                            label: "Name",
                            name: "name",
                            type: "textfield",
                        },

                        {
                            isRequired: true,
                            label: "Serial",
                            name: "serial",
                            type: "textfield",
                        },

                        {
                            name: "branch",
                            type: "autocomplete",
                            label: "Branch",
                            isRequired: true,
                            autocomplete: {
                                labelKey: "name",
                                label: "Branch",
                                api:
                                    user?.user_type === User_Type.Superadmin
                                        ? "/organization/branch/superadmin"
                                        : "/organization/branch",
                                defaultValue: "default_branch",
                                apiParams: {
                                    organization:
                                        user?.user_type === User_Type.Admin
                                            ? user.organization.code
                                            : filter.org_code,
                                },
                                request_headers: {
                                    "x-org":
                                        user?.user_type === User_Type.Admin
                                            ? user.organization.code
                                            : filter.org_code,
                                },
                            },
                        } as any,

                        // {
                        //     name: "organization",
                        //     type: "autocomplete",
                        //     label: "Organization",
                        //     isRequired: true,
                        //     autocomplete: {
                        //         labelKey: "name",
                        //         label: "Organization",
                        //         api: "/organization",
                        //         defaultValue: "default_organization",
                        //         apiParams: {
                        //             code:
                        //                 user?.user_type === User_Type.Admin
                        //                     ? user.organization.code
                        //                     : undefined,
                        //         },
                        //     },
                        // } as any,
                    ]}
                    customCols={[
                        {
                            header: "sync_device",
                            content: (row) =>
                                user?.user_type === User_Type.Superadmin ? (
                                    <Box>
                                        {/* <MultiPermissionAuthorize
                                        ops={["SYNC DEVICE USERS"]}
                                    > */}
                                        <Sync_Device_Users
                                            device={row}
                                            org_code={filter.org_code}
                                        />
                                        {/* </MultiPermissionAuthorize> */}

                                        {/* <Sync_Branch_Attendance
                                    device={row}
                                    org_code={filter.org_code}
                                /> */}

                                        {/* <MultiPermissionAuthorize
                                        ops={["SYNC DEVICE ATTENDANCE"]}
                                    > */}
                                        <Sync_Device_Attendance
                                            device={row}
                                            org_code={filter.org_code}
                                        />
                                        {/* </MultiPermissionAuthorize> */}
                                    </Box>
                                ) : null,
                        },
                    ]}
                />
            )}
        </Box>
    );
}
